import { ExamCenterProctorType } from '@exam-center/graphql';
import {
  ProctorTypeField,
  ProctorPersonalInformationFields,
  ProctorEmployeeInformationFields,
  ProctorDocumentFields,
} from '@exam-center/proctor-request-management';
import { Divider, Flex, PaperSection, Text } from '@shared/components';
import { formatDate } from '@shared/utils';

import { useGetAgreementStorageObjectsFile } from '../../hooks';
import { useAdminProctorDetails } from '../../providers';

function getProctorEndDateTexts(hasEndedAt: boolean) {
  if (hasEndedAt) {
    return {
      firstText: 'กำหนดวันที่สิ้นสุดปฏิบัติหน้าที่',
      secondText: 'ระบุวันที่สิ้นสุดปฏิบัติหน้าที่ด้วยตนเอง',
    };
  }

  return {
    firstText: 'ไม่มีกำหนด',
    secondText: 'ยังไม่มีกำหนดวันที่สิ้นสุดปฏิบัติหน้าที่',
  };
}

const PROCTOR_TYPE_TEXTS: Record<ExamCenterProctorType, string> = {
  [ExamCenterProctorType.Proctor_1]: 'ผู้คุมสอบประเภทที่ 1',
  [ExamCenterProctorType.Proctor_2]: 'ผู้คุมสอบประเภทที่ 2',
  [ExamCenterProctorType.Proctor_3]: 'ผู้คุมสอบประเภทที่ 3',
};

export default function AdminProctorDetailsReadonlyField() {
  const { proctor } = useAdminProctorDetails();

  const hasEndedAt = !!proctor.endedAt;
  const { firstText, secondText } = getProctorEndDateTexts(hasEndedAt);

  const agreementStorageObjectsFile = useGetAgreementStorageObjectsFile({
    proctor,
  });

  return (
    <PaperSection title='ข้อมูลผู้คุมสอบ' sx={{ borderRadius: (theme) => theme.shape.rounded(2) }}>
      <Flex
        sx={(theme) => ({
          flex: 1,
          gap: theme.spacing(6),
          flexDirection: 'column',
          p: theme.spacing(6),
        })}
      >
        <ProctorTypeField
          fields={{
            proctorType: {
              mode: 'view',
              value: <Text>{PROCTOR_TYPE_TEXTS[proctor.type]}</Text>,
            },
          }}
        />
        <Divider />
        <ProctorPersonalInformationFields
          fields={{
            idNumber: {
              mode: 'view',
              value: proctor.idNumber,
            },
            titleTh: {
              mode: 'view',
              value: proctor.titleTh,
            },
            firstNameTh: {
              mode: 'view',
              value: proctor.firstNameTh,
            },
            lastNameTh: {
              mode: 'view',
              value: proctor.lastNameTh,
            },
            titleEn: {
              mode: 'view',
              value: proctor.titleEn ? proctor.titleEn : '-',
            },
            firstNameEn: {
              mode: 'view',
              value: proctor.firstNameEn ? proctor.firstNameEn : '-',
            },
            lastNameEn: {
              mode: 'view',
              value: proctor.lastNameEn ? proctor.lastNameEn : '-',
            },
            phoneNumber: {
              mode: 'view',
              value: proctor.phoneNumber ? proctor.phoneNumber : '-',
            },
            email: {
              mode: 'view',
              value: proctor.email ? proctor.email : '-',
            },
            address: {
              mode: 'view',
              value: proctor.address ? proctor.address : '-',
            },
          }}
        />
        <Divider />
        <ProctorEmployeeInformationFields
          fields={{
            startedAt: {
              mode: 'view',
              value: proctor.startedAt ? formatDate(proctor.startedAt) : '-',
              slotProps: {
                label: 'วันที่เริ่มปฏิบัติหน้าที่',
              },
            },
            code: {
              mode: 'view',
              value: proctor.metadata?.employeeId ? proctor.metadata?.employeeId : '-',
              slotProps: {
                label: 'รหัสพนักงาน',
              },
            },
            position: {
              mode: 'view',
              value: proctor.metadata?.position ? proctor.metadata?.position : '-',
              slotProps: {
                label: 'ตำแหน่ง',
              },
            },
            department: {
              mode: 'view',
              value: proctor.metadata?.department ? proctor.metadata?.department : '-',
              slotProps: {
                label: 'ฝ่ายงาน/ส่วนงาน',
              },
            },
            isTerminationAssign: {
              mode: 'view',
              value: (
                <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
                  <Text>{firstText}</Text>
                  <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant='caption'>
                    {secondText}
                  </Text>
                </Flex>
              ),
            },
            endedAt: {
              mode: 'view',
              value: proctor.endedAt ? formatDate(proctor.endedAt) : '-',
              slotProps: {
                label: 'วันที่สิ้นสุดปฏิบัติหน้าที่',
              },
            },
          }}
          options={{
            shouldShowEndedAtDateDisplay: hasEndedAt,
          }}
        />
        <Divider />
        <ProctorDocumentFields
          fields={{
            agreementForms: {
              mode: 'view',
              slotProps: {
                files: agreementStorageObjectsFile,
              },
            },
          }}
          hideIdentificationFiles={true}
        />
      </Flex>
    </PaperSection>
  );
}
